import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

import { PipeModule } from '@shared/pipes/pipe.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MarkdownModule } from 'ngx-markdown';

// COMPONENTS
import { FooterComponent } from './footer/footer.component';
import { NavbarComponent } from './navbar/navbar.component';
import { HorizontalMenuComponent } from './horizontal-menu/horizontal-menu.component';
import { VerticalMenuComponent } from './vertical-menu/vertical-menu.component';
import { CustomizerComponent } from './customizer/customizer.component';
import { NotificationSidebarComponent } from './notification-sidebar/notification-sidebar.component';
import { DkExportComponent } from './components/dk-export/dk-export.component';
import { DkDetailComponent } from './components/dk-detail/dk-detail.component';
import { CustomerSelectComponent } from './components/customer-select/customer-select.component';
import { TableColumnHeaderComponent } from './components/table-column-header/table-column-header.component';
import { TaskStatusBadgeComponent } from './components/task-status-badge/task-status-badge.component';
import { TaskTypeBadgeComponent } from './components/task-type-badge/task-type-badge.component';
import { UserDisplayComponent } from './components/user-display/user-display.component';
import { TaskDueDateComponent } from './components/task-due-date/task-due-date.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { AttachmentItemComponent } from './components/attachment-item/attachment-item.component';
import { AgentChatComponent } from './components/agent-chat/agent-chat.component';

// DIRECTIVES
import { ToggleFullscreenDirective } from './directives/toggle-fullscreen.directive';
import { SidebarLinkDirective } from './directives/sidebar-link.directive';
import { SidebarDropdownDirective } from './directives/sidebar-dropdown.directive';
import { SidebarAnchorToggleDirective } from './directives/sidebar-anchor-toggle.directive';
import { SidebarDirective } from './directives/sidebar.directive';
import { TopMenuDirective } from './directives/topmenu.directive';
import { TopMenuLinkDirective } from './directives/topmenu-link.directive';
import { TopMenuDropdownDirective } from './directives/topmenu-dropdown.directive';
import { TopMenuAnchorToggleDirective } from './directives/topmenu-anchor-toggle.directive';
import { FileDragDropDirective } from './directives/file-drag-drop.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ConfirmDirective } from './directives/confirm.directive';

// PIPES
import { HqDatePipe } from './pipes/hq-date.pipe';
import { StringHqDatePipe } from './pipes/string-hq-date.pipe';
import { TelPipe } from './pipes/tel.pipe';
import { LocalDatePipe } from './pipes/local-date.pipe';
import { TimePipe } from './pipes/time.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';

// API SERVICE
import { AuthApiService } from './apis/auth-api.service';
import { UsersApiService } from './apis/users-api.service';
import { FileDownloadApiService } from './apis/file-download-api.service';
import { LookupApiService } from './apis/lookup-api.service';
import { MasterDataApiService } from './apis/master-data-api.service';
import { UploadApiService } from './apis/upload-api.service';
import { FieldErrorComponent } from './components/field-error/field-error.component';
import { DkAuthComponent } from './components/dk-auth/dk-auth.component';
import { DkDateRangeComponent } from './components/dk-daterange/dk-date-range.component';
import { NgOtpInputModule } from 'ng-otp-input';
import { provideNgxMask } from 'ngx-mask';

@NgModule({
  exports: [
    CommonModule,
    FooterComponent,
    NavbarComponent,
    VerticalMenuComponent,
    HorizontalMenuComponent,
    CustomizerComponent,
    NotificationSidebarComponent,
    NgbModule,
    TranslateModule,
    DkExportComponent,
    DkDateRangeComponent,
    DkDetailComponent,
    DkAuthComponent,
    HqDatePipe,
    StringHqDatePipe,
    TelPipe,
    LocalDatePipe,
    TimePipe,
    SafeHtmlPipe,
    FieldErrorComponent,
    NgOtpInputModule,
    NgxSpinnerModule,
    ToggleFullscreenDirective,
    SidebarDirective,
    SidebarLinkDirective,
    SidebarDropdownDirective,
    SidebarAnchorToggleDirective,
    TopMenuLinkDirective,
    TopMenuDropdownDirective,
    TopMenuAnchorToggleDirective,
    TopMenuDirective,
    CustomerSelectComponent,
    TableColumnHeaderComponent,
    TaskStatusBadgeComponent,
    TaskTypeBadgeComponent,
    UserDisplayComponent,
    TaskDueDateComponent,
    EmptyStateComponent,
    FileDragDropDirective,
    AutofocusDirective,
    ClickOutsideDirective,
    ConfirmDirective,
    PipeModule,
    AttachmentItemComponent,
    AgentChatComponent
  ],
  imports: [
    CommonModule,
    RouterModule,
    NgbModule,
    TranslateModule,
    FormsModule,
    OverlayModule,
    ReactiveFormsModule,
    //PerfectScrollbarModule,
    PipeModule,
    NgOtpInputModule,
    NgxSpinnerModule,
    MarkdownModule.forRoot(),
  ],
  providers: [
    AuthApiService,
    UsersApiService,
    FileDownloadApiService,
    LookupApiService,
    MasterDataApiService,
    UploadApiService,
    provideNgxMask()
  ],
  declarations: [
    FooterComponent,
    NavbarComponent,
    VerticalMenuComponent,
    HorizontalMenuComponent,
    CustomizerComponent,
    NotificationSidebarComponent,
    ToggleFullscreenDirective,
    SidebarLinkDirective,
    SidebarDropdownDirective,
    SidebarAnchorToggleDirective,
    SidebarDirective,
    TopMenuLinkDirective,
    TopMenuDropdownDirective,
    TopMenuAnchorToggleDirective,
    TopMenuDirective,
    DkExportComponent,
    DkDateRangeComponent,
    DkDetailComponent,
    DkAuthComponent,
    HqDatePipe,
    StringHqDatePipe,
    TelPipe,
    LocalDatePipe,
    TimePipe,
    SafeHtmlPipe,
    FieldErrorComponent,
    CustomerSelectComponent,
    TableColumnHeaderComponent,
    TaskStatusBadgeComponent,
    TaskTypeBadgeComponent,
    UserDisplayComponent,
    TaskDueDateComponent,
    EmptyStateComponent,
    FileDragDropDirective,
    AutofocusDirective,
    ClickOutsideDirective,
    ConfirmDirective,
    AttachmentItemComponent,
    AgentChatComponent
  ]
})
export class SharedModule {
}
